import React, { useCallback, useEffect, useState } from 'react';
import t from 'react-translate';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { translationUpdate } from 'redux/actions/users';

import { useAppDispatch } from 'redux/store';
import { getCurrentUser } from 'redux/selectors/users';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { getLectureComponent } from 'redux/selectors/timeline';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { gray5, white } from 'styles/global_defaults/colors';
import { doubleSpacing, halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { VideoType } from 'redux/schemas/models/video';


type EditableProps = {
  editInfo: TranslationInfo,
  onSaveRef: (fn: () => void) => void,
  setSavingDisabled: (boolean) => void,
};

const EditableAudioVideoList = ({
  editInfo,
  onSaveRef,
  setSavingDisabled,
}: EditableProps) => {
  const lectureComponent = useSelector((state) => (
    getLectureComponent(state, editInfo.id)
  )) as NLectureComponent<ComponentType.VIDEO>;
  const videoIdsArray = lectureComponent.lectureVideos;
  const videosArray = videoIdsArray.map(videoId => useSelector((state) => ({
    ...state.models.video[state.models.lectureVideos[videoId].video],
    lectureVideoId: state.models.lectureVideos[videoId].id,
  })));
  const initialState = {
    content: lectureComponent.content,
    videosArray,
  };
  const [videoList, setVideoList] = useState(initialState);
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useAppDispatch();

  const editableDiscussionStyle = css`
    width: 100%;
    .bs4-form-control {
      background: ${white};
    }
    .border-simple-text {
      border-radius: ${quarterSpacing}px;
      border-color: ${gray5};
    }
    margin: 0 ${doubleSpacing}px;
    .bs4-form-control {
      background: ${white};
    }
    .tag {
      margin: ${halfSpacing}px 0;
      padding: 3px 12px 3px;
      border-radius: ${halfSpacing}px;
      height: ${standardSpacing}px;
      width: fit-content;
      &.tag-extra-space {
        margin-top: ${standardSpacing}px;
      }
    }
  `;

  const save = useCallback(() => {
    const { id } = editInfo;
    const attributes = [];
    if (videoList.content !== initialState.content) {
      attributes.push({
        translationId: lectureComponent.translationIds.content,
        name: 'content',
        text: videoList.content,
        elementType: lectureComponent.type,
        elementId: lectureComponent.id,
      });
    }
    videoList.videosArray.forEach((video, videoIndex) => {
      if (video.name !== initialState.videosArray[videoIndex]?.name) {
        attributes.push({
          translationId: video.translationIds.name,
          name: 'name',
          text: video.name,
          elementType: lectureComponent.type,
          elementId: video.lectureVideoId,
        });
      }
    });
    const payload = {
      id,
      attributes,
    };
    dispatch(translationUpdate(payload)).then(() => {
      dispatch(closeEditTranslationComponent());
    });
  }, [videoList, currentUser.translationPreferenceLanguage, dispatch, editInfo]);

  const updateVideo = (index, name) => {
    const newVideosArray = [...videoList.videosArray];
    newVideosArray[index] = {
      ...newVideosArray[index],
      name,
    };
    setVideoList({
      ...videoList,
      videosArray: newVideosArray,
    });
  };

  useEffect(() => {
    onSaveRef(() => save);
  }, [onSaveRef, save]);

  useEffect(() => {
    if (initialState !== videoList) {
      setSavingDisabled(false);
    }
    return (() => {
      setSavingDisabled(true);
    });
  }, [videoList]);

  const getTitleText = (file, index) => {
    if (file.type === VideoType.VIDEO) {
      return videoList.videosArray.length === 1 ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.VIDEO_NAME() : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.VIDEO_NUMBER_NAME(`${index + 1}`);
    }
    return videoList.videosArray.length === 1 ? t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.AUDIO_NAME() : t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.AUDIO_NUMBER_NAME(`${index + 1}`);
  };


  return (
    <div className='editable-discussion' css={editableDiscussionStyle}>
      {
        videoList.content && (
          <React.Fragment>
            <div className='tag bg-gray-6 text-small font-weight-bolder'>
              { t.AUTOMATED_TRANSLATION.EDIT_TRANSLATION.TITLE() }
            </div>
            <NvTextArea
              value={videoList.content}
              autoComplete='off'
              textareaClassName='text-large-body border-simple-text'
              onChange={(e) => {
                setVideoList({
                  ...videoList,
                  content: e.currentTarget?.value,
                });
              }}
            />
          </React.Fragment>
        )
      }
      {
        videoList.videosArray.map((video, index) => (
          <React.Fragment>
            <div className={`tag bg-gray-6 text-small font-weight-bolder ${videoList.content || index > 0 ? 'tag-extra-space' : ''}`}>
              {
                getTitleText(video, index)
              }
            </div>
            <NvTextArea
              value={video.name}
              autoComplete='off'
              textareaClassName='text-large-body border-simple-text'
              onChange={(e) => {
                updateVideo(index, e.currentTarget?.value);
              }}
            />
          </React.Fragment>
        ))
      }
    </div>
  );
};

export default EditableAudioVideoList;
